import { useState } from "react";
import { toast } from "sonner";
import { Loader2, MapPin, Send } from "lucide-react";
import { postEnquiry } from "@/lib/site";
import { Reveal, SectionTag } from "@/components/Reveal";

const TOPICS = ["Kronegekko", "Spørgsmål om produkt", "Specialbygget terrarium", "Andet"];

const field =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-cream placeholder:text-sage/50 outline-none transition-colors duration-300 focus:border-forest";

export default function KontaktPage() {
  const [form, setForm] = useState({ name: "", email: "", topic: TOPICS[0], message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Udfyld venligst navn, e-mail og besked.");
      return;
    }
    setSending(true);
    try {
      await postEnquiry(form);
      toast.success("Besked sendt — vi svarer som regel inden for en dag.");
      setForm({ name: "", email: "", topic: TOPICS[0], message: "" });
    } catch {
      toast.error("Noget gik galt. Prøv igen.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="kontakt-page" className="mx-auto max-w-6xl px-6 pb-24 pt-36 lg:px-16">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
        <Reveal>
          <SectionTag index="07" label="Kontakt" />
          <h1 className="mt-6 font-serif text-4xl tracking-tight text-cream sm:text-5xl">
            Besøg eller skriv til os
          </h1>
          <div className="mt-10 border border-forest/40 bg-forest/10 p-8">
            <p className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-pale">
              <MapPin className="h-4 w-4 text-forest" />
              Afhentningsadresse
            </p>
            <p className="mt-4 font-serif text-3xl text-cream">Fælleden 16</p>
            <p className="font-serif text-3xl text-cream">7190 Billund</p>
            <p className="mt-4 text-sm leading-relaxed text-sage">
              Afhentning sker efter aftale — vi kontakter dig, når din ordre er betalt.
            </p>
          </div>
          <div className="mt-10 space-y-2 font-mono text-xs uppercase tracking-[0.2em] text-sage">
            <p>GEKKOFUEL · Frederik &amp; Sesilie</p>
            <p>Webshop · gekkofuel.dk</p>
            <p>Svar som regel inden for en dag</p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="kontakt-form"
            onSubmit={submit}
            className="space-y-5 border border-white/10 bg-moss p-8"
          >
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-pale">
              Skriv til os
            </p>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <input
                data-testid="kontakt-name-input"
                value={form.name}
                onChange={set("name")}
                placeholder="Dit navn"
                className={field}
              />
              <input
                data-testid="kontakt-email-input"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="Din e-mail"
                className={field}
              />
            </div>
            <select
              data-testid="kontakt-topic-select"
              value={form.topic}
              onChange={set("topic")}
              className={`${field} appearance-none`}
            >
              {TOPICS.map((t) => (
                <option key={t} value={t} className="bg-ink">
                  {t}
                </option>
              ))}
            </select>
            <textarea
              data-testid="kontakt-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder="Hvad kan vi hjælpe med?"
              rows={5}
              className={`${field} resize-none`}
            />
            <button
              data-testid="kontakt-submit-button"
              type="submit"
              disabled={sending}
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-forest py-4 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink disabled:opacity-60"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Send besked
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
